import WhatsNewBell from "@/shared/components/WhatsNew/WhatsNewBell";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContactColumns,
  renderContactCell,
} from "@/shared/utils/contactConfig";
import { Trash } from "lucide-react";
import ContactFiltersUI from "./ContactFiltersUI";
import ContactHeader from "./ContactHeader";
import ContactTableSection from "./ContactTableSection";
import GroupedContactTableSection from "./GroupedContactTableSection";
import FieldMappingModal from "./FieldMappingModal";
import ContactEmptyStates from "./ContactEmptyStates";
// import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import UploadModal from "@/shared/components/ui/Upload/UploadModal";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import Loader from "@/shared/components/ui/Loaders/Loader";
import useContactsPage from "./useContactsPage";
import AddContactModal from "./AddContactModal";
import EditContactModal from "./EditContactModal";
import CampaignsListModal from "./CampaignsListModal";
import MatchScoreReviewModal from "./MatchScoreReviewModal";
import SelectionInfoBanner from "./SelectionInfoBanner";
import ConsentUpdateModal from "./ConsentUpdateModal";
import TrialRemainingCount from "@/shared/components/ui/TrialRemainingCount";

const ContactMainComponent = () => {
  const ctx = useContactsPage();
  const navigate = useNavigate();
  // Finding new contacts happens on the Find page now.
  const openFindContacts = useCallback(() => navigate("/prospecting/contacts"), [navigate]);

  const contactShareActions = useMemo(
    () => [
      // {
      //   id: 1,
      //   name: "Share",
      //   onClick: () => {},
      //   icon: <Share size={16} color="#252B37" />,
      // },
      // {
      //   id: 2,
      //   name: "Download Analytics",
      //   onClick: () => {},
      //   icon: <Download size={16} color="#252B37" />,
      // },
      {
        id: 1,
        name: "Delete",
        onClick: () => {
          if (ctx.selectedContacts.length > 0) {
            ctx.setShowDeleteConfirmModal(true);
          }
        },
        icon: <Trash size={16} color="#ef4444" />,
      },
    ],
    [ctx],
  );

  const hasSearchOrFilters =
    ctx.searchValue.trim().length > 0 ||
    Object.keys(ctx.appliedFilters).length > 0;
  const showHeader =
    ["DATA", "EMPTY_SEARCH", "EMPTY_FILTERS"].includes(ctx.viewState) ||
    (ctx.viewState === "LOADING" && hasSearchOrFilters);

  const handleInitialImport = useCallback(
    async (e, data, file) => {
      if (!file) return;
      const success = await ctx.handleCSVFileUpload(file);
      if (success) ctx.setShow(false);
    },
    [ctx],
  );

  const handleAddImport = useCallback(
    async (e, data, file) => {
      if (!file) return;
      const success = await ctx.handleCSVFileUpload(file);
      if (success) ctx.setShowAddModal(false);
    },
    [ctx],
  );

  return (
    <div className="flex flex-col px-4">
      <div className="flex justify-between items-center w-full">
        <div className="flex items-center gap-2">
          <h1 className="text-[1.375rem] font-bold text-gray-800 dark:text-gray-100">Contacts</h1>
          <TrialRemainingCount resource="contacts" />
        </div>
        <WhatsNewBell compact className="hidden md:flex" />
        {/* {ctx.displayContacts?.length > 0 && (
          <CommonDropdownMenu actions={contactShareActions} />
        )} */}
      </div>

      <div className="w-full mt-4">
        {(ctx.viewState === "EMPTY_INITIAL" ||
          (ctx.viewState === "LOADING" && !hasSearchOrFilters)) && (
            <ContactEmptyStates
              viewState={ctx.viewState}
              show={ctx.show}
              setShow={ctx.setShow}
              onImport={handleInitialImport}
              searchValue={ctx.searchValue}
              appliedFilters={ctx.appliedFilters}
              onAddContactClick={() => {
                ctx.setShow(false);
                ctx.setShowAddContactForm(true);
              }}
              onRelevantAddClick={openFindContacts}
            />
          )}

        <div className="flex flex-col gap-0.5 w-full ">
          {showHeader && (
            <ContactHeader
              searchValue={ctx.searchValue}
              onSearchChange={ctx.handleSearchChange}
              totalContacts={
                ctx.isGrouped ? ctx.totalGroups : ctx.totalContacts
              }
              currentPage={ctx.currentPage}
              rowsPerPage={ctx.rowsPerPage}
              loadedCount={
                ctx.isGrouped
                  ? ctx.displayGroups.length
                  : ctx.displayContacts.length
              }
              appliedFilterCount={ctx.appliedFilterCount}
              selectedCount={ctx.selectedContacts.length}
              selectionSummary={
                ctx.isEntireListSelected
                  ? ctx.totalContacts > ctx.contactsSelectionLimit
                    ? `${ctx.selectedContacts.length.toLocaleString()} selected (max ${ctx.contactsSelectionLimit.toLocaleString()} per batch; ${ctx.totalContacts.toLocaleString()} match filters)`
                    : `All ${ctx.selectedContacts.length.toLocaleString()} contact(s) in this list selected`
                  : ""
              }
              groupBy={ctx.groupBy}
              onSetGroupBy={ctx.setGroupByMode}
              onFilterClick={() => ctx.setShowfilters(true)}
              onImportClick={() => ctx.setShowAddModal(true)}
              onCampaignClick={ctx.handleMoveTo}
              isUploading={ctx.isUploading}
              onClearSelection={ctx.clearSelection}
              onDeleteClick={() => ctx.setShowDeleteConfirmModal(true)}
              isBulkDeleting={ctx.isBulkDeleting}
              isMoving={ctx.isMoving}
              onGenerateMatchScore={ctx.handleGenerateMatchScore}
              isGeneratingMatchScore={ctx.isGeneratingMatchScore}
              matchScoreDisabled={ctx.hasNoCampaigns}
              itemName={ctx.isGrouped ? "group" : "contact"}
            />
          )}

          {showHeader && (
            <div className="flex flex-col h-[calc(100dvh-160px)]">
              {ctx.viewState === "LOADING" && hasSearchOrFilters && (
                <div className="flex items-center justify-center h-[calc(100dvh-220px)] animate-fade-in">
                  <Loader text="Loading contacts..." />
                </div>
              )}

              {(ctx.viewState === "EMPTY_SEARCH" ||
                ctx.viewState === "EMPTY_FILTERS") && (
                  <div className="animate-fade-in">
                    <ContactEmptyStates
                      viewState={ctx.viewState}
                      searchValue={ctx.searchValue}
                      appliedFilters={ctx.appliedFilters}
                    />
                  </div>
                )}

              {ctx.viewState === "DATA" && (
                <div
                  className={`flex flex-col gap-2 min-h-0 flex-1 relative animate-fade-in-up transition-[opacity,filter] duration-300 ease-out ${(ctx.isFetchingContacts || ctx.isFetchingGroups) &&
                    !ctx.isLoadingContacts &&
                    !ctx.isLoadingGroups
                    ? "opacity-75 brightness-[0.98]"
                    : "opacity-100 brightness-100"
                    }`}
                >
                  {/* Active Filter Chips */}
                  {/* {Object.keys(ctx.appliedFilters).length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 pb-2 pt-2 border-b border-gray-100 mb-2 w-full justify-end">
                      <span className="text-[0.675rem] text-gray-500 font-semibold uppercase tracking-wider mr-1">
                        Active filters:
                      </span>
                      {Object.entries(ctx.appliedFilters).map(
                        ([key, value]) => {
                          const chips = Array.isArray(value) ? value : [value];
                          return chips.map((chip, idx) => {
                            let displayLabel = String(chip);
                            if (key === "matchScoreFit") {
                              const bucketLabels = {
                                strong: "Strong Fit",
                                moderate: "Medium Fit",
                                not_fit: "No Fit",
                                unavailable: "Unavailable",
                              };
                              displayLabel = bucketLabels[chip] ?? displayLabel;
                            } else if (key === "enrollmentStatus") {
                              displayLabel =
                                chip === "enrolled"
                                  ? "Enrolled in Campaign"
                                  : "Not Enrolled";
                            } else if (key === "dateCreated") {
                              const dateLabels = {
                                today: "Today",
                                last_7_days: "Last 7 Days",
                                last_30_days: "Last 30 Days",
                                last_90_days: "Last 90 Days",
                              };
                              displayLabel = dateLabels[chip] ?? displayLabel;
                            } else if (key === "companySize") {
                              displayLabel = `Size: ${chip}`;
                            }

                            const displayKey = key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase());

                            return (
                              <span
                                key={`${key}-${idx}`}
                                className="inline-flex items-center gap-1 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-lg border border-gray-200 transition-colors"
                              >
                                <span className="text-[10px] text-gray-400 font-medium">
                                  {displayKey}:
                                </span>
                                <span className="font-semibold">
                                  {displayLabel}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    ctx.removeSingleFilterValue(key, chip)
                                  }
                                  className="text-gray-400 hover:text-red-500 ml-1 shrink-0 transition-colors"
                                >
                                  <X size={12} strokeWidth={2.5} />
                                </button>
                              </span>
                            );
                          });
                        },
                      )}
                      <button
                        type="button"
                        onClick={ctx.clearFilters}
                        className="text-xs text-secondary hover:text-secondary-light font-semibold ml-1.5 transition-colors"
                      >
                        Clear all
                      </button>
                    </div>
                  )} */}

                  {/* Selection Info Banner */}
                  <SelectionInfoBanner ctx={ctx} />

                  {/* Contact Table Section */}
                  {ctx.isGrouped ? (
                    <GroupedContactTableSection
                      groups={ctx.displayGroups}
                      expandedGroups={ctx.expandedGroups}
                      openGroups={ctx.openGroups}
                      onToggleExpand={ctx.toggleGroupExpand}
                      onLoadMore={ctx.loadMoreGroupContacts}
                      columns={ContactColumns}
                      renderCell={(item, columnKey) =>
                        renderContactCell(
                          item,
                          columnKey,
                          ctx.handleDelete,
                          ctx.handleEdit,
                          ctx.openConsentUpdate,
                        )
                      }
                      totalCount={ctx.totalGroups}
                      rowsPerPage={ctx.rowsPerPage}
                      page={ctx.currentPage}
                      onPageChange={ctx.setCurrentPage}
                      selectedContacts={ctx.selectedContacts}
                      onSelectionChange={ctx.handleSelectionChange}
                      sortDescriptor={ctx.sortDescriptor}
                      onSortChange={ctx.setSortDescriptor}
                      isEntireListSelected={ctx.isEntireListSelected}
                      selectedGroupIds={ctx.selectedGroupIds}
                      onToggleGroupSelect={ctx.toggleGroupSelection}
                      onSelectNextInGroup={ctx.selectNextInGroup}
                      batchSelectingGroupId={ctx.batchSelectingGroupId}
                    />
                  ) : (
                    <ContactTableSection
                      contacts={ctx.displayContacts}
                      columns={ContactColumns}
                      renderCell={(item, columnKey) =>
                        renderContactCell(
                          item,
                          columnKey,
                          ctx.handleDelete,
                          ctx.handleEdit,
                          ctx.openConsentUpdate,
                        )
                      }
                      totalContacts={ctx.totalContacts}
                      rowsPerPage={ctx.rowsPerPage}
                      page={ctx.currentPage}
                      onPageChange={ctx.setCurrentPage}
                      selectedContacts={ctx.selectedContacts}
                      onSelectionChange={ctx.handleSelectionChange}
                      showSelectAllBanner={ctx.selectedContacts.length > 0}
                    />
                  )}
                </div>
              )}

              <ContactFiltersUI
                showfilters={ctx.showfilters}
                setShowfilters={ctx.setShowfilters}
                filters={ctx.filters}
                setFilters={ctx.setFilters}
                appliedFilters={ctx.appliedFilters}
                clearFilters={ctx.clearFilters}
                onUpdate={ctx.applyFilters}
              />
            </div>
          )}
        </div>
      </div>

      {ctx.showDeleteConfirmModal && (
        <DeleteConfirmationModal
          showConfirmDelete={ctx.showDeleteConfirmModal}
          onClose={() => {
            ctx.setShowDeleteConfirmModal(false);
            ctx.setDeletingContactId(null);
            ctx.clearSelection();
          }}
          title={`Are you sure you want to delete ${ctx.deletingContactId ? "this contact" : ctx.selectedContacts.length > 1 ? `${ctx.selectedContacts.length} contacts` : "this contact"}?`}
          description={`Deleting ${ctx.deletingContactId ? "this contact" : ctx.selectedContacts.length > 1 ? `${ctx.selectedContacts.length} contacts` : "this contact"} will permanently remove it from your list - This action can't be undone.`}
          onDelete={
            ctx.deletingContactId
              ? ctx.handleDeleteContact
              : ctx.handleBulkDelete
          }
          deleteButtonText={
            ctx.deletingContactId
              ? "Yes, delete contact"
              : ctx.selectedContacts.length > 1
                ? `Yes, delete ${ctx.selectedContacts.length} contacts`
                : `Yes, delete contact`
          }
          cancelButtonText="I changed my mind"
          width="450px"
          isDeleting={
            ctx.deletingContactId ? ctx.isDeleting : ctx.isBulkDeleting
          }
        />
      )}

      <ConsentUpdateModal
        isOpen={ctx.showConsentModal}
        contacts={ctx.consentTargets}
        onClose={ctx.closeConsentUpdate}
        onSubmit={ctx.handleConsentUpdate}
        isSubmitting={ctx.isUpdatingConsent}
      />

      {ctx.showAddModal && (
        <UploadModal
          show={ctx.showAddModal}
          variant="add"
          onClose={() => ctx.setShowAddModal(false)}
          onImport={handleAddImport}
          onAddContactClick={() => {
            ctx.setShowAddModal(false);
            ctx.setShowAddContactForm(true);
          }}
          onRelevantAddClick={openFindContacts}
        />
      )}

      {ctx.showAddContactForm && (
        <AddContactModal
          onClose={() => {
            ctx.setShowAddContactForm(false);
            if (ctx.viewState === "EMPTY_INITIAL") {
              ctx.setShow(true);
            }
          }}
          isOpen={ctx.showAddContactForm}
        />
      )}
      {ctx.showEditContactForm && (
        <EditContactModal
          onClose={() => {
            ctx.setShowEditContactForm(false);
            ctx.setEditingContact(null);
          }}
          isOpen={ctx.showEditContactForm}
          contact={ctx.editingContact}
        />
      )}
      {ctx.showCampaigns && (
        <CampaignsListModal
          isOpen={ctx.showCampaigns}
          onClose={ctx.resetSelectionAndClose}
          contactsToAdd={ctx.selectedContacts}
          moveMode={ctx.moveMode}
          onCampaignSelected={ctx.handleCampaignSelected}
          isScoring={ctx.isGeneratingMoveScores}
          isMoving={ctx.isMoving}
        />
      )}
      {ctx.showReview && (
        <MatchScoreReviewModal
          isOpen={ctx.showReview}
          contacts={ctx.reviewContacts}
          campaign={ctx.selectedCampaign}
          onClose={ctx.resetSelectionAndClose}
          onBack={ctx.handleReviewBack}
          backButtonLabel="Back to campaign selection"
          onConfirm={(contacts) =>
            ctx.executeCampaignMove(ctx.selectedCampaign, ctx.moveMode, contacts)
          }
          isEnrolling={ctx.isMoving}
        />
      )}
      <FieldMappingModal
        isOpen={ctx.fieldMappingOpen}
        onClose={ctx.closeFieldMappingModal}
        headers={ctx.csvPreviewData?.headers ?? []}
        sampleRows={ctx.csvPreviewData?.sampleRows ?? []}
        onConfirmImport={ctx.confirmCSVImportWithMapping}
        onCheckImport={ctx.checkCSVImport}
        isSubmitting={ctx.isUploading}
      />

    </div>
  );
};

export default ContactMainComponent;
